import React, { useRef } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppColor from "utilities/AppColor";
import { CONSENT_STATUS } from "config/AppConfig";
import { createOrUpdateConsent } from "store/slices/client";
import moment from "moment";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: "1rem",
  },
  fileName: {
    fontSize: 13,
    color: "#374151",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileLabel: {
    fontSize: 14,
    color: "#000",
    fontWeight: 500,
    marginBottom: "0.5rem",
  },
  documentLink: {
    fontSize: 12,
    color: "#0D9488",
    textDecoration: "underline",
    cursor: "pointer",
  },
}));

const AddConsentModal = ({ open, onClose, clientId, companyId, onSuccess, initialData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const isEditMode = Boolean(initialData?.id);

  const [documentFile, setDocumentFile] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setDocumentFile(null);
    }
  }, [open]);

  const statusOptions = [
    { value: CONSENT_STATUS.PENDING, label: t("consentStatus.Pending") },
    { value: CONSENT_STATUS.SIGNED, label: t("consentStatus.Signed") },
    { value: CONSENT_STATUS.VERBAL, label: t("consentStatus.Verbal") },
  ];

  const fileName = documentFile?.name ? documentFile.name : (initialData?.fileName ? initialData?.fileName : t("riskConsent.noDocument"));

  const initialValues = {
    name: initialData?.name || "",
    status: initialData?.status || CONSENT_STATUS.PENDING,
    reviewDate: initialData?.reviewDate ? moment(initialData.reviewDate).format("YYYY/MM/DD") : "",
    verbalConsentDate: initialData?.verbalConsentDate ? moment(initialData.verbalConsentDate).format("YYYY/MM/DD") : "",
    verbalConsentMethod: initialData?.verbalConsentMethod || "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required(t("validation.required")).max(255, t("validation.max_length")),
    status: Yup.string().required(t("validation.required")),
    verbalConsentDate: Yup.string().when("status", {
      is: CONSENT_STATUS.VERBAL,
      then: Yup.string().required(t("validation.required")),
    }),
    verbalConsentMethod: Yup.string().when("status", {
      is: CONSENT_STATUS.VERBAL,
      then: Yup.string().trim().required(t("validation.required")),
    }),
  });

  const handleFileChange = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ];
      if (!allowedTypes.includes(file.type)) {
        return;
      }
      setDocumentFile(file);
      // Auto-set status to Signed when a document is uploaded
      // if (setFieldValue) {
      //   setFieldValue("status", CONSENT_STATUS.SIGNED);
      // }
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      companyId,
      clientId,
      name: values.name.trim(),
      status: values.status,
      reviewDate: values.reviewDate || null,
    };

    // Verbal consent fields
    if (values.status === CONSENT_STATUS.VERBAL) {
      payload.verbalConsentRecorded = true;
      payload.verbalConsentDate = values.verbalConsentDate || null;
      payload.verbalConsentMethod = values.verbalConsentMethod || null;
    }

    // Document file (optional)
    if (documentFile) {
      payload.documentFile = documentFile;
    }

    if (isEditMode) {
      payload.id = initialData.id;
    }

    const result = await dispatch(createOrUpdateConsent(payload));
    setSubmitting(false);

    if (createOrUpdateConsent.fulfilled.match(result)) {
      onSuccess && onSuccess();
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? t("riskConsent.editConsent") : t("riskConsent.addConsent")}
      maxWidth="sm"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              {/* ── Section: Consent Details ── */}
              <Typography className={classes.sectionTitle}>
                {t("riskConsent.sectionDetails")}
              </Typography>

              <div className="row">
                <div className="col-sm-12">
                  <Field
                    component={AppInput}
                    name="name"
                    label="riskConsent.consentName"
                    placeholder="riskConsent.consentNamePlaceholder"
                    disabled={initialData?.default}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={SelectField}
                    name="status"
                    label="riskConsent.status"
                    options={statusOptions}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={CustomDatePicker}
                    name="reviewDate"
                    label="riskConsent.reviewDate"
                    formatDateJob={true}
                  />
                </div>
              </div>

              {/* ── Section: Document Upload ── */}
              <Typography className={classes.sectionTitle}>
                {t("riskConsent.sectionDocument")}
              </Typography>

              <Box className={classes.fileLabel}>{t("riskConsent.documentFile")}</Box>
              <Box className={classes.fileBox}>
                <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444" }} />
                <Box className={classes.fileName}>
                  {fileName}
                </Box>
                <AppButton
                  label={documentFile || initialData?.documentUrl ? t("button.change") : t("button.upload")}
                  mode="outlined"
                  rounder
                  style={{ fontSize: 11, textTransform: "none", height: 28 }}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.value = null;
                      fileInputRef.current.click();
                    }
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/jpg,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChange(e, setFieldValue)}
                />
              </Box>

              {/* ── Section: Verbal Consent (shown when status = Verbal) ── */}
              {values.status === CONSENT_STATUS.VERBAL && (
                <>
                  <Typography className={classes.sectionTitle}>
                    {t("riskConsent.sectionVerbal")}
                  </Typography>

                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={CustomDatePicker}
                        name="verbalConsentDate"
                        label="riskConsent.verbalConsentDate"
                        formatDateJob={true}
                        required
                      />
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={AppInput}
                        name="verbalConsentMethod"
                        label="riskConsent.verbalConsentMethod"
                        placeholder="riskConsent.verbalConsentMethodPlaceholder"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ── Actions ── */}
              <Box className={classes.control}>
                <AppButton
                  label={t("button.cancel")}
                  onClick={onClose}
                  buttonColor="#F3F4F6"
                  rounder
                  style={{ color: "#111827" }}
                  disabled={isSubmitting}
                />
                <AppButton
                  label={isEditMode ? t("button.save_changes") : t("riskConsent.saveConsent")}
                  rounder
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  buttonColor={AppColor.mainColor}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default AddConsentModal;
